import { useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  RefreshCw,
  TrendingUp,
  TrendingDown,
  ExternalLink,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  Plug,
  BarChart3,
} from "lucide-react";
import {
  reports as reportsApi,
  type ReportChannel,
  type ReportsSummary,
  type TopPost,
  type TrendPoint,
} from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import { Sparkline } from "@/components/Sparkline";
import { CadenceHeatmap } from "@/components/CadenceHeatmap";
import { SectionHeader } from "@/components/SectionHeader";
import { fmtDate } from "@/lib/content";
import { useAuth } from "@/lib/AuthContext";
import { can } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import { useIsSparkle } from "@/lib/useTheme";
import { MetricBlob, NeonPill, ScreenTitle } from "@/components/sparkle/kit";
import { Sparkle as GSparkle, Star5 as GStar, Heart as GHeart } from "@/components/sparkle/glyphs";

const RANGES = [7, 30, 90] as const;

function fmtNum(n: number | null | undefined): string {
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return (n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1) + "M";
  if (abs >= 1_000) return (n / 1_000).toFixed(abs >= 10_000 ? 0 : 1) + "k";
  return String(Math.round(n));
}

function relTime(unix: number | null): string {
  if (!unix) return "never";
  const secs = Math.floor(Date.now() / 1000) - unix;
  if (secs < 90) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 86400 * 7) return `${Math.floor(secs / 86400)}d ago`;
  return fmtDate(unix);
}

/** Recent-half vs earlier-half delta of a daily series (Monarch-style chip). */
function halfDelta(series: number[]): number | undefined {
  if (series.length < 4) return undefined;
  const mid = Math.floor(series.length / 2);
  const earlier = series.slice(0, mid).reduce((s, v) => s + v, 0);
  const recent = series.slice(mid).reduce((s, v) => s + v, 0);
  return recent - earlier;
}

export function ReportsPage() {
  const { user } = useAuth();
  const canEdit = can(user, "edit_operational");
  const queryClient = useQueryClient();
  const sparkle = useIsSparkle();
  const [days, setDays] = useState<number>(30);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["reports", days],
    queryFn: () => reportsApi.summary(days),
  });

  const syncMut = useMutation({
    mutationFn: () => reportsApi.sync(days),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["reports"] }),
  });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        {sparkle ? (
          <ScreenTitle
            eyebrow="Kovach Consulting · Reports"
            title="Reports"
            star={<GStar size={24} color="#FFD400" />}
            sub="Engagement across every integration — straight from the source."
          />
        ) : (
          <div>
            <h1 className="text-3xl font-bold">Reports.</h1>
            <p className="text-muted-foreground mt-1">
              Engagement across every connected channel — pulled straight from the source.
            </p>
          </div>
        )}
        <div className="flex items-center gap-2 no-print">
          {sparkle ? (
            <div className="flex items-center gap-1.5">
              {RANGES.map((r) => (
                <NeonPill key={r} color="#9B2BFF" active={days === r} onClick={() => setDays(r)}>
                  {r}d
                </NeonPill>
              ))}
            </div>
          ) : (
            <div className="flex rounded-[10px] border overflow-hidden">
              {RANGES.map((r) => (
                <button
                  key={r}
                  onClick={() => setDays(r)}
                  className={cn(
                    "px-3 py-1.5 text-sm font-medium transition-colors",
                    days === r ? "bg-foreground text-background" : "text-foreground hover:bg-muted",
                  )}
                >
                  {r}d
                </button>
              ))}
            </div>
          )}
          {canEdit ? (
            <Button size="sm" variant="outline" onClick={() => syncMut.mutate()} disabled={syncMut.isPending}>
              <RefreshCw size={14} className={cn("mr-1.5", syncMut.isPending && "animate-spin")} />
              {syncMut.isPending ? "Syncing…" : "Sync now"}
            </Button>
          ) : null}
          <Button size="sm" variant="outline" onClick={() => window.print()}>
            Export PDF
          </Button>
        </div>
      </header>

      {isLoading ? (
        <div className="text-muted-foreground text-sm">Loading…</div>
      ) : isError ? (
        <div className="text-destructive text-sm">
          {error instanceof Error ? error.message : "Couldn't load reports."}
        </div>
      ) : data ? (
        <ReportsBody data={data} syncing={syncMut.isPending} onSync={() => syncMut.mutate()} canEdit={canEdit} />
      ) : null}
    </div>
  );
}

function ReportsBody({
  data,
  syncing,
  onSync,
  canEdit,
}: {
  data: ReportsSummary;
  syncing: boolean;
  onSync: () => void;
  canEdit: boolean;
}) {
  const hasConnections = data.totals.connections > 0;
  const hasData = data.channels.length > 0 || data.top_posts.length > 0;

  if (!hasConnections) {
    return (
      <EmptyState
        icon={Plug}
        label="No integrations connected"
        hint="Connect your Meta (Facebook + Instagram) and TikTok accounts to pull engagement into one board."
        cta={
          <Button asChild size="sm">
            <Link to="/settings">Connect in Settings →</Link>
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>
          {data.totals.connections} connected {data.totals.connections === 1 ? "account" : "accounts"}
        </span>
        <span aria-hidden>·</span>
        <span>Last synced {relTime(data.last_synced_at)}</span>
        <span aria-hidden>·</span>
        <span>Since {data.range.since}</span>
      </div>

      {!hasData ? (
        <EmptyState
          icon={BarChart3}
          label="Connected — no metrics yet"
          tone="muted"
          hint="Your accounts are linked. Run the first sync to pull the latest engagement, or wait for the nightly refresh."
          cta={
            canEdit ? (
              <Button size="sm" onClick={onSync} disabled={syncing}>
                <RefreshCw size={14} className={cn("mr-1.5", syncing && "animate-spin")} />
                {syncing ? "Syncing…" : "Run first sync"}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <Hero data={data} />
          <KpiRow data={data} />
          <TimeCard trend={data.trend} days={data.range.days} />
          <ChannelSection channels={data.channels} />
          <TopPosts posts={data.top_posts} />
        </>
      )}
    </div>
  );
}

function Hero({ data }: { data: ReportsSummary }) {
  const totalEng = data.totals.engagements + data.totals.post_engagement;
  const series = data.trend.map((t) => t.engagements);
  const delta = halfDelta(series);
  return (
    <Card className="border-l-4 border-l-primary">
      <CardContent className="p-5 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="sm:w-56 shrink-0">
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">
            Total engagement
          </div>
          <div className="text-4xl font-bold mt-1 tabular-nums leading-none">{fmtNum(totalEng)}</div>
          <div className="mt-2 flex items-center gap-2 text-xs">
            {delta != null && delta !== 0 ? (
              <span
                className={cn(
                  "inline-flex items-center gap-1 font-medium",
                  delta > 0 ? "text-success" : "text-destructive",
                )}
              >
                {delta > 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                {delta > 0 ? "+" : ""}
                {fmtNum(delta)}
              </span>
            ) : null}
            <span className="text-muted-foreground">last {data.range.days} days</span>
          </div>
        </div>
        <div className="flex-1 min-w-0 text-primary">
          <Sparkline values={series} height={56} className="h-14" />
        </div>
      </CardContent>
    </Card>
  );
}

function KpiRow({ data }: { data: ReportsSummary }) {
  const sparkle = useIsSparkle();
  const t = data.totals;
  if (sparkle) {
    const chg = t.followers_change;
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricBlob
          label="Followers"
          value={fmtNum(t.followers)}
          color="#FF2D9B"
          icon={<GHeart size={18} color="#fff" />}
          sub={chg ? `${chg > 0 ? "+" : ""}${fmtNum(chg)} this period` : undefined}
          rotate={-1.2}
        />
        <MetricBlob label="Reach" value={fmtNum(t.reach)} color="#9B2BFF" icon={<GSparkle size={18} color="#fff" />} rotate={1.4} />
        <MetricBlob label="Impressions" value={fmtNum(t.impressions)} color="#00D5E0" icon={<GStar size={18} color="#fff" />} rotate={-1} />
        <MetricBlob
          label="Posts"
          value={fmtNum(t.posts)}
          color="#10E0B4"
          icon={<GSparkle size={18} color="#fff" />}
          sub={`${fmtNum(t.post_views)} views`}
          rotate={1.2}
        />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <KpiTile label="Followers" value={fmtNum(t.followers)} delta={t.followers_change} accent="primary" />
      <KpiTile label="Reach" value={fmtNum(t.reach)} accent="secondary" />
      <KpiTile label="Impressions" value={fmtNum(t.impressions)} accent="accent" />
      <KpiTile label="Posts" value={fmtNum(t.posts)} sub={`${fmtNum(t.post_views)} views`} accent="muted" />
    </div>
  );
}

const ACCENT_BORDER: Record<string, string> = {
  primary: "border-l-primary",
  secondary: "border-l-secondary",
  accent: "border-l-accent",
  muted: "border-l-border",
};

function KpiTile({
  label,
  value,
  delta,
  sub,
  accent,
}: {
  label: string;
  value: string;
  delta?: number;
  sub?: string;
  accent: keyof typeof ACCENT_BORDER | string;
}) {
  return (
    <Card className={cn("border-l-4", ACCENT_BORDER[accent] ?? "border-l-border")}>
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">
          {label}
        </div>
        <div className="text-2xl font-bold mt-1 tabular-nums">{value}</div>
        {delta != null && delta !== 0 ? (
          <div
            className={cn(
              "text-xs font-medium mt-0.5 flex items-center gap-1",
              delta > 0 ? "text-success" : "text-destructive",
            )}
          >
            {delta > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {delta > 0 ? "+" : ""}
            {fmtNum(delta)} this period
          </div>
        ) : sub ? (
          <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function TimeCard({ trend, days }: { trend: TrendPoint[]; days: number }) {
  const values: Record<string, number> = {};
  for (const t of trend) values[t.date] = t.engagements;
  const empty = trend.every((t) => t.reach === 0 && t.impressions === 0 && t.engagements === 0);

  const minis: { label: string; values: number[]; total: number; tone: string }[] = [
    { label: "Reach", values: trend.map((t) => t.reach), total: trend.reduce((s, t) => s + t.reach, 0), tone: "text-secondary" },
    { label: "Impressions", values: trend.map((t) => t.impressions), total: trend.reduce((s, t) => s + t.impressions, 0), tone: "text-accent" },
    { label: "Engagement", values: trend.map((t) => t.engagements), total: trend.reduce((s, t) => s + t.engagements, 0), tone: "text-primary" },
  ];

  return (
    <Card>
      <CardContent className="p-5 space-y-5">
        <div>
          <SectionHeader title="Engagement calendar" />
          {empty ? (
            <div className="text-sm text-muted-foreground">
              No day-level account insights in this range yet. The calendar fills in as syncs accumulate.
            </div>
          ) : (
            <CadenceHeatmap values={values} days={days} />
          )}
        </div>
        <div className="grid grid-cols-3 gap-4 border-t pt-4">
          {minis.map((m) => (
            <div key={m.label}>
              <div className="flex items-baseline justify-between">
                <span className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">
                  {m.label}
                </span>
                <span className="text-sm font-bold tabular-nums">{fmtNum(m.total)}</span>
              </div>
              <div className={cn("mt-1.5", m.tone)}>
                <Sparkline values={m.values} height={28} className="h-7" />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function channelCardStyle(color: string | null, sparkle: boolean): CSSProperties {
  const style: CSSProperties = { borderLeftColor: color ?? "hsl(var(--primary))" };
  if (sparkle && color) {
    // Soft, channel-tinted glow — calmer + harmonized with the left edge,
    // replacing the heavy generic purple sticker glow on these dense cards.
    style.boxShadow = `0 1px 2px ${color}1f, 0 10px 22px -14px ${color}55`;
  }
  return style;
}

function ChannelSection({ channels }: { channels: ReportChannel[] }) {
  const sparkle = useIsSparkle();
  const withEng = channels.map((c) => ({ ...c, eng: c.engagements + c.post_engagement }));
  const sorted = [...withEng].sort((a, b) => b.eng - a.eng);
  const totalEng = sorted.reduce((s, c) => s + c.eng, 0) || 1;

  return (
    <div>
      <SectionHeader title="By integration" count={channels.length} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {sorted.map((c) => {
          const avg = c.posts > 0 ? Math.round(c.post_engagement / c.posts) : null;
          const share = Math.round((c.eng / totalEng) * 100);
          return (
            <Card key={c.channel_id ?? c.name} className="border-l-4" style={channelCardStyle(c.color, sparkle)}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block size-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: c.color ?? "#999" }}
                  />
                  <span className="font-semibold">{c.name}</span>
                  <Badge variant="muted" className="ml-auto">
                    {fmtNum(c.followers)} followers
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                  <Metric label="Reach" value={fmtNum(c.reach)} />
                  <Metric label="Impressions" value={fmtNum(c.impressions)} />
                  <Metric label="Engagement" value={fmtNum(c.eng)} />
                  <Metric label="Posts" value={fmtNum(c.posts)} />
                </div>
                <div className="space-y-1 border-t pt-2">
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>{share}% of engagement</span>
                    {avg != null ? <span>{fmtNum(avg)} avg/post</span> : null}
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${share}%`, backgroundColor: c.color ?? "hsl(var(--primary))" }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </div>
  );
}

function TopPosts({ posts }: { posts: TopPost[] }) {
  if (posts.length === 0) return null;
  return (
    <div>
      <SectionHeader title="Top posts" count={posts.length} />
      <Card>
        <CardContent className="p-0 divide-y">
          {posts.map((p, i) => (
            <div key={p.id} className="flex items-start gap-3 p-4">
              <span className="text-xs font-bold tabular-nums text-muted-foreground w-5 shrink-0 pt-1 text-right">
                {i + 1}
              </span>
              <span
                className="inline-block size-2.5 rounded-full shrink-0 mt-1.5"
                style={{ backgroundColor: p.channel_color ?? "#999" }}
                title={p.channel_name ?? undefined}
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium line-clamp-2">
                  {p.caption?.trim() || <span className="text-muted-foreground italic">Untitled</span>}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                  {p.channel_name ? <span>{p.channel_name}</span> : null}
                  {p.posted_at ? <span>{fmtDate(p.posted_at)}</span> : null}
                  {p.views != null ? <PostStat icon={Eye} value={p.views} /> : null}
                  <PostStat icon={Heart} value={p.likes ?? 0} />
                  <PostStat icon={MessageCircle} value={p.comments ?? 0} />
                  <PostStat icon={Share2} value={p.shares ?? 0} />
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-lg font-bold tabular-nums leading-none">{fmtNum(p.engagement)}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">engagement</div>
                {p.permalink ? (
                  <a
                    href={p.permalink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center text-xs text-primary hover:underline mt-1"
                  >
                    Open <ExternalLink size={11} className="ml-0.5" />
                  </a>
                ) : null}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function PostStat({ icon: Icon, value }: { icon: typeof Eye; value: number }) {
  return (
    <span className="inline-flex items-center gap-1 tabular-nums">
      <Icon size={12} />
      {fmtNum(value)}
    </span>
  );
}
